import { CButtonClassname, EButtonColor, Section } from '@web_opxp/components';
import { ProgressSelectors, useTranslation } from '@web_opxp/features';
import uniq from 'lodash/uniq';
import * as React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import LevelSection from '../../components/LevelSection/LevelSection';
import SideStory from '../../components/SideStory/SideStory';
import ExercisesContainer from '../../components/exercises/ExercisesContainer';
import { CyclesSelectors } from '../../cycles';
import { DashboardRoute } from '../../dashboard/Dashboard';
import { SessionActions, SessionSelectors } from '../../session';
import { IState } from '../../store/state';
import { postSessionBoosterOpened } from '../firebaseActions';
import SessionPageCard from './SessionPageCard';

interface ICardProps {
  workoutId: number;
  delta?: number;
  index: number;
  locked: boolean;
}

export interface ISessionPageProps {
  sessionId: number; // current session id used to show amount of completed sessions
  cycleLength: number; // amount of sessions in current cycle
  progressWorkouts: ICardProps[]; // array of workouts with positive delta
  noProgressWorkouts: ICardProps[]; // array of workouts without positive delta
  skippedWorkouts: ICardProps[]; // array of workouts witch were skipped
  domain: number;
  onContinue: () => void;
  onBoosterLink: () => void;
}

export const SessionPageFC: React.FC<ISessionPageProps> = ({
  sessionId,
  cycleLength,
  noProgressWorkouts,
  progressWorkouts,
  skippedWorkouts,
  domain,
  onContinue,
  onBoosterLink,
}) => {
  const { t } = useTranslation();

  const getExercisesContainer = (exercises: ICardProps[], title: string) =>
    exercises.length ? (
      <ExercisesContainer title={t(title)}>
        {exercises.map((workout) => (
          <SessionPageCard key={`${workout.index}-${workout.workoutId}`} {...workout} />
        ))}
      </ExercisesContainer>
    ) : null;

  return (
    <>
      <LevelSection
        title={t('Exercise.SessionView.Title')}
        subtitle={t('Exercise.SessionView.Description', { sessionId, cycleLength })}>
        <Link className={`${CButtonClassname} ${EButtonColor.white}`} to={DashboardRoute} onClick={() => onContinue()}>
          {t('Exercise.SessionView.Link')}
        </Link>
        <Link className={`${CButtonClassname} ${EButtonColor.white}`} to="/extras" onClick={onBoosterLink}>
          {t('Exercise.SessionView.ExtrasLink')}
        </Link>
      </LevelSection>
      <Section>
        <div className="row">
          <div className="col-6">
            {getExercisesContainer(progressWorkouts, 'Exercise.SessionView.BestTitle')}
            {getExercisesContainer(noProgressWorkouts, 'Exercise.SessionView.OtherTitle')}
            {getExercisesContainer(skippedWorkouts, 'Exercise.SessionView.SkippedTitle')}
          </div>
          <div className="col-2" />
          <div className="col-4">
            <SideStory domain={domain} />
          </div>
        </div>
      </Section>
    </>
  );
};

const mapStateToProps = (state: IState) => {
  const sessionId = CyclesSelectors.getCurrentSessionId(state);
  const cycleLength = CyclesSelectors.getCycleLength(state);
  const workoutIds = SessionSelectors.getSessionWorkoutsForUniqueExercises(state);

  const progressWorkouts: ICardProps[] = [];
  const noProgressWorkouts: ICardProps[] = [];
  const skippedWorkouts: ICardProps[] = [];
  uniq(workoutIds).forEach((workoutId, index) => {
    const disabled = SessionSelectors.isWorkoutDisabledById(state, workoutId);
    if (disabled) {
      // skip workout if its disabled
      return;
    }
    const currentScore = ProgressSelectors.getLastWorkoutScore(state, workoutId);
    const lastScore = ProgressSelectors.getLastWorkoutScore(state, workoutId, 2);
    const delta = currentScore - lastScore;
    const locked = !!SessionSelectors.isWorkoutLocked(state, workoutId);
    if (locked) {
      skippedWorkouts.push({ workoutId, index, locked });
    } else if (delta && delta > 0) {
      progressWorkouts.push({ workoutId, delta, index, locked });
    } else {
      noProgressWorkouts.push({ workoutId, index, locked });
    }
  });

  const domain = SessionSelectors.getSessionDomain(state);
  return {
    sessionId,
    cycleLength,
    progressWorkouts,
    noProgressWorkouts,
    skippedWorkouts,
    domain,
  };
};

const mapDispatchToProps = {
  onContinue: SessionActions.reloadSession,
  onBoosterLink: postSessionBoosterOpened,
};

const SessionPage = connect(mapStateToProps, mapDispatchToProps)(SessionPageFC);

export default SessionPage;
